import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { DEMO } from '../lib/demo';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, ArrowRight, Loader2, ThumbsUp, Eye, EyeOff, FileText } from 'lucide-react';
import { ReportBody } from '../pages/dashboard/WeeklyReports';

const noteColor = (n) => (n >= 7 ? '#37d39a' : n >= 5 ? '#ffcf6b' : '#ff8a5b');

function NoteBadge({ label, note }) {
  if (note == null) return <span className="text-[11px] text-slate-500">{label} —</span>;
  return <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: noteColor(note) + '22', color: noteColor(note) }}>{label} {note}/10</span>;
}

const DEMO_ALL = {
  chatters: [
    { chatter_id: 'ch1', chatter_name: 'Lina', result: { analysis: { note: 7, points_faibles: ['Donne le prix trop tôt'] }, relationnel: { note: 8 }, stats: { ca: 1840 } } },
    { chatter_id: 'ch2', chatter_name: 'Sam', result: { analysis: { note: 5, points_faibles: ['Manque de rareté'] }, relationnel: { note: 6 }, stats: { ca: 1460 } } },
    { chatter_id: 'ch3', chatter_name: 'Max', result: { analysis: { note: 4, points_faibles: ['Ne relance pas assez'] }, relationnel: { note: 7 }, stats: { ca: 1180 } } },
  ],
};

// Tableau de bord (owner/manager) : tous les chatteurs, notes vente/relationnel, sans cliquer.
export function ChatterAnalysisDashboard() {
  const { hasPerm } = useAuth();
  const canToggle = typeof hasPerm === 'function' && hasPerm('manage_telegram');
  const [data, setData] = useState(DEMO ? DEMO_ALL : null);
  const [loading, setLoading] = useState(!DEMO);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (DEMO) { setVis(false); return; }
    api.get('/reports/chatter-analyses-all', { params: { days: 30 } })
      .then(({ data }) => { setData(data); setVis(!!data.visible_to_chatters); }).catch(() => setData({ chatters: [] })).finally(() => setLoading(false));
  }, []);
  const toggleVis = async () => {
    const next = !vis; setVis(next);
    try { await api.post('/reports/chatter-analysis-visibility', { visible: next }); }
    catch { setVis(!next); }
  };

  const rows = (data?.chatters || []).slice().sort((a, b) => ((b.result?.stats?.ca || 0) - (a.result?.stats?.ca || 0)));
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-semibold text-white flex items-center gap-2"><TrendingUp size={16} className="text-indigo-400" /> Analyse des chatteurs</h3>
        <div className="flex items-center gap-3">
          {canToggle && (
            <button onClick={toggleVis} title="Rendre l'analyse visible dans l'espace de chaque chatteur"
              className={`text-[12px] flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-colors ${vis ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-white/[0.04] text-slate-400 hover:text-white'}`}>
              {vis ? <Eye size={13} /> : <EyeOff size={13} />} {vis ? 'Visible par les chatteurs' : 'Caché aux chatteurs'}
            </button>
          )}
          <Link to="/dashboard/employees/analysis" className="text-[12px] text-indigo-300 hover:text-indigo-200 flex items-center gap-1">Voir en détail <ArrowRight size={12} /></Link>
        </div>
      </div>
      {loading ? <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> chargement…</div>
        : rows.length === 0 ? <div className="text-slate-500 text-sm">Les analyses se préparent automatiquement (mises à jour toutes les quelques heures).</div>
          : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rows.map((r) => {
                const v = r.result?.analysis; const rel = r.result?.relationnel;
                const faible = (v?.points_faibles || [])[0] || (rel?.points_faibles || [])[0];
                return (
                  <Link key={r.chatter_id} to="/dashboard/employees/analysis" className="rounded-xl border border-white/10 bg-white/[0.02] p-3 hover:border-indigo-500/40 transition-colors block">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-semibold text-white truncate">{r.chatter_name}</span>
                      <span className="text-[11px] text-emerald-300 shrink-0">{(r.result?.stats?.ca || 0).toLocaleString('fr-FR')} €</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <NoteBadge label="💰" note={v?.note} />
                      <NoteBadge label="🤝" note={rel?.note} />
                    </div>
                    {faible ? <div className="text-[11px] text-slate-400 truncate">🔧 {faible}</div>
                      : <div className="text-[11px] text-slate-600">analyse en préparation…</div>}
                  </Link>
                );
              })}
            </div>
          )}
    </div>
  );
}

const DEMO_MINE = {
  stats: { ca: 1840, nb_ventes: 24 },
  analysis: { note: 7, resume: 'Tu donnes envie, mais tu dis le prix trop tôt.', points_forts: [{ point: 'Tu crées bien le désir', exemple: 'attends je te montre un truc que personne a vu 😈' }], a_ameliorer: [{ probleme: 'Prix annoncé trop tôt', au_lieu_de: "j'ai un truc pour toi c'est 30€", dis_plutot: "j'ai filmé un truc rien que pour toi… 😏" }] },
  relationnel: { note: 8, points_forts: [{ point: 'Tu es chaleureuse et présente', exemple: 'coucou toi 🥰 j\'ai pensé à toi aujourd\'hui' }], a_ameliorer: [] },
};

const weekLabel = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    const e = new Date(d); e.setDate(d.getDate() + 6);
    const f = (x) => x.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    return `${f(d)} → ${f(e)}`;
  } catch { return iso; }
};

// Espace du chatteur : SON rapport hebdo (top ANONYME — le backend ne renvoie jamais le nom).
export function MyWeeklyReportCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!DEMO);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (DEMO) return;
    api.get('/reports/my-weekly')
      .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  if (DEMO || loading) return null;
  if (!data || data.hidden || !(data.reports || []).length) return null;
  const reports = data.reports;
  const r = reports[Math.min(idx, reports.length - 1)];
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-semibold text-white flex items-center gap-2"><FileText size={16} className="text-sky-300" /> Mon rapport hebdo</h3>
        <select value={idx} onChange={(e) => setIdx(Number(e.target.value))}
          className="bg-white/5 border border-white/10 rounded-lg px-2 text-xs text-white h-8 outline-none">
          {reports.map((x, i) => <option key={x.week_start} value={i} className="bg-[#0B0B0E]">Semaine du {weekLabel(x.week_start)}</option>)}
        </select>
      </div>
      {r.is_top && (
        <div className="mb-4 rounded-xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-transparent p-3.5 flex items-center gap-3">
          <span className="text-2xl">🏆</span>
          <div>
            <div className="text-sm font-bold text-amber-300">Meilleure performance de la semaine — c'est TOI !</div>
            <div className="text-[11px] text-amber-200/70">Top vendeur de l'équipe sur cette semaine. Continue comme ça 🔥</div>
          </div>
        </div>
      )}
      <ReportBody r={r} showTop={false} />
    </div>
  );
}

// Espace du chatteur : SON propre bilan (motivant).
export function MyAnalysisCard() {
  const [data, setData] = useState(DEMO ? DEMO_MINE : null);
  const [loading, setLoading] = useState(!DEMO);
  useEffect(() => {
    if (DEMO) return;
    api.get('/reports/my-analysis', { params: { days: 30 } })
      .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Ton analyse se prépare…</div>;
  if (!data || (!data.analysis && !data.relationnel)) return null;
  const dims = [['💰 Vente', data.analysis], ['🤝 Relationnel', data.relationnel]].filter((d) => d[1]);
  return (
    <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/[0.05] p-5">
      <h3 className="font-semibold text-white flex items-center gap-2 mb-4"><ThumbsUp size={16} className="text-indigo-300" /> Ton analyse par l'IA — comment progresser</h3>
      <div className="space-y-4">
        {dims.map(([label, A]) => (
          <div key={label}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-white">{label}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: noteColor(A.note) + '22', color: noteColor(A.note) }}>{A.note}/10</span>
            </div>
            {(A.points_forts || []).slice(0, 2).map((p, i) => {
              const point = typeof p === 'string' ? p : (p?.point || '');
              const ex = typeof p === 'string' ? '' : (p?.exemple || '');
              if (!point) return null;
              return (
                <div key={'pf' + i} className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] p-2.5 mt-1.5">
                  <div className="text-[12px] text-emerald-300">💪 {point}</div>
                  {ex && <div className="text-[11px] text-slate-400 italic mt-1">« {ex} »</div>}
                </div>
              );
            })}
            {(A.a_ameliorer || []).slice(0, 2).map((x, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-black/20 p-2.5 mt-1.5">
                <div className="text-[12px] text-white mb-1">{x.probleme}</div>
                <div className="text-[11px] text-rose-300/90">❌ {x.au_lieu_de}</div>
                <div className="text-[11px] text-emerald-300">✅ {x.dis_plutot}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
